import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

// Risk thresholds for the current workspace.
//
// GET s1/api/thresholds/tenant/:tenantId does the layering server-side: it reads the
// global category defaults, finds this tenant's overrides, and returns each category
// with a resolved `rules` set plus the `default_rules` it came from. So a category is
// always renderable — `mode` says whether what you are looking at is this workspace's
// own choice or the shipped default.
//
// Nothing is seeded or created from here. The 70 categories are global and come from the
// scan pipeline; a tenant can only re-band them or hand one back to its default.
export function useThresholds() {
  const [tenantId, setTenantId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // The tenant id comes from s1/api/tenant, NOT from s1/api/me. /me selects tenant_id in
  // its query but does not put it in the response, so reading it off the shell's `me`
  // yields undefined and every request below would silently never fire.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tenant = await api.get("s1/api/tenant");
        if (!cancelled) setTenantId(tenant?.tenant_id ?? null);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Could not identify this workspace.");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      // The scan groups are only used to order and label the sections, so a failure
      // there must not take the categories with it — `scan_type` on each category is
      // enough to group by on its own.
      const [data, groupData] = await Promise.all([
        api.get(`s1/api/thresholds/tenant/${tenantId}`),
        api.get("s1/api/thresholds/scan-groups").catch(() => null),
      ]);
      setCategories(data?.data || []);
      setGroups(groupData?.data || []);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load the risk thresholds.");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Writing an override for this workspace. The whole three-tier set goes up together:
  // the API stores `custom_thresholds` as one JSON document, so sending a single tier
  // would drop the other two.
  const save = useCallback(async (categoryId, customThresholds) => {
    try {
      await api.put(`s1/api/thresholds/tenant/${tenantId}/category/${categoryId}`, {
        custom_thresholds: customThresholds,
      });
      await load();
      return true;
    } catch (e) {
      setError(e.message || "Could not save those thresholds.");
      return false;
    }
  }, [tenantId, load]);

  // Deletes the override row, which is what "back to default" means here — there is no
  // copy of the defaults to write back, and writing one would freeze this category at
  // today's values instead of following the global default forward.
  const reset = useCallback(async (categoryId) => {
    try {
      await api.del(`s1/api/thresholds/tenant/${tenantId}/category/${categoryId}`);
      await load();
      return true;
    } catch (e) {
      setError(e.message || "Could not reset that category.");
      return false;
    }
  }, [tenantId, load]);

  // --- Global catalogue edits ------------------------------------------------
  //
  // EVERYTHING BELOW IS PLATFORM-WIDE. report_item_categories and scan_groups carry no
  // tenant_id: creating, moving or deleting one changes it for every workspace on the
  // installation, not just this one. The band editing above is per tenant; this is not,
  // and the screen has to say so rather than letting the two look alike.
  const mutate = useCallback(async (fn, failure) => {
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(e.message || failure);
      return false;
    }
  }, [load]);

  const createCategory = useCallback((fields) => mutate(
    () => api.post("s1/api/thresholds/categories", fields),
    "Could not create that category."
  ), [mutate]);

  // The API takes no name here — findingsGenerator looks categories up BY NAME, so a
  // rename would quietly stop a category scoring. Bands, icon and group only.
  const updateCategory = useCallback((id, fields) => mutate(
    () => api.put(`s1/api/thresholds/categories/${id}`, fields),
    "Could not update that category."
  ), [mutate]);

  const moveCategory = useCallback((id, scanType) => mutate(
    () => api.put(`s1/api/thresholds/categories/${id}/move`, { scan_type: scanType || null }),
    "Could not move that category."
  ), [mutate]);

  const deleteCategory = useCallback((id) => mutate(
    () => api.del(`s1/api/thresholds/categories/${id}`),
    "Could not delete that category."
  ), [mutate]);

  const createGroup = useCallback((fields) => mutate(
    () => api.post("s1/api/thresholds/scan-groups", fields),
    "Could not create that group."
  ), [mutate]);

  // No COALESCE on the server: every visual column is written from this payload, so the
  // caller has to send the whole set or blank what it leaves out.
  const updateGroup = useCallback((id, fields) => mutate(
    () => api.put(`s1/api/thresholds/scan-groups/${id}`, fields),
    "Could not update that group."
  ), [mutate]);

  const deleteGroup = useCallback((id) => mutate(
    () => api.del(`s1/api/thresholds/scan-groups/${id}`),
    "Could not delete that group."
  ), [mutate]);

  return {
    categories, groups, loading, error, reload: load, save, reset,
    createCategory, updateCategory, moveCategory, deleteCategory,
    createGroup, updateGroup, deleteGroup,
  };
}
